import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, computed, inject, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { CategoriesStore, CategoryCardVm } from '@state/categories.store';
import { SearchResponse, SearchService } from '@core/services/search.service';
import { CommunityColorKey, resolveCommunityColorKey } from '@shared/constants';
import { CommunityIconComponent } from '@shared/components/community-icon/community-icon.component';
import { Subject, catchError, debounceTime, distinctUntilChanged, map, of, switchMap, takeUntil } from 'rxjs';

type SearchCondition = {
  name: string;
  color: string;
  communityKey: CommunityColorKey;
};

type SearchTopic = {
  name: string;
  slug: string;
  usageCount: number;
  communityKey: CommunityColorKey;
};

type SearchCommunityResult = {
  id: string;
  name: string;
  highlightedName: string;
  slug?: string;
  memberCount: number;
  postCount: number;
  communityKey: CommunityColorKey;
};

type SearchTagResult = {
  name: string;
  highlightedName: string;
  slug: string;
  usageCount: number;
  communityId: string;
  communityName: string;
  communityKey: CommunityColorKey;
};

type SearchPostResult = {
  id: string;
  title: string;
  highlightedTitle: string;
  authorName: string;
  replyCount: number;
};

export type SearchSelection = {
  kind: 'community' | 'topic' | 'post' | 'query';
  term: string;
  route: any[];
  params?: Record<string, string | number | null>;
};

@Component({
  selector: 'app-search-dropdown',
  standalone: true,
  imports: [CommonModule, MatIconModule, CommunityIconComponent],
  templateUrl: './search-dropdown.component.html',
  styleUrls: ['./search-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchDropdownComponent implements OnInit, OnDestroy {
  private readonly searchService = inject(SearchService);
  private readonly categoriesStore = inject(CategoriesStore);
  private readonly destroy$ = new Subject<void>();
  private readonly searchSubject = new Subject<string>();

  private readonly contextValue = signal<'global' | 'community'>('global');
  private readonly currentCommunityIdValue = signal<string | undefined>(undefined);
  private readonly currentCommunityNameValue = signal<string | undefined>(undefined);
  private readonly searchQueryValue = signal('');

  @Input() set context(value: 'global' | 'community') {
    this.contextValue.set(value ?? 'global');
  }

  @Input() set currentCommunityId(value: string | undefined) {
    this.currentCommunityIdValue.set(value);
  }

  @Input() set currentCommunityName(value: string | undefined) {
    this.currentCommunityNameValue.set(value);
  }

  @Input() set searchQuery(value: string) {
    const normalized = value ?? '';
    this.searchQueryValue.set(normalized);
    this.searchSubject.next(normalized);
  }

  get context(): 'global' | 'community' {
    return this.contextValue();
  }

  get searchQuery(): string {
    return this.searchQueryValue();
  }

  @Output() suggestionSelected = new EventEmitter<SearchSelection>();
  @Output() closed = new EventEmitter<void>();

  readonly isSearching = signal(false);
  readonly recentSearches = signal<string[]>([]);
  readonly matchingPosts = signal<SearchPostResult[]>([]);

  readonly defaultConditions: SearchCondition[] = [
    { name: 'Diabetes', color: '#4a90d9', communityKey: 'diabetes' },
    { name: 'Hypothyroidism', color: '#e07a5f', communityKey: 'thyroid' },
    { name: 'PCOS', color: '#2a9d8f', communityKey: 'pcos' },
    { name: 'Acne', color: '#c65a8a', communityKey: 'skin' },
    { name: 'Pigmentation', color: '#c65a8a', communityKey: 'skin' },
    { name: 'Anxiety', color: '#7c5cbf', communityKey: 'mental' },
    { name: 'Depression', color: '#7c5cbf', communityKey: 'mental' },
    { name: "Hashimoto's", color: '#e07a5f', communityKey: 'thyroid' },
    { name: 'Hyperthyroidism', color: '#e07a5f', communityKey: 'thyroid' },
    { name: 'Insulin Resistance', color: '#4a90d9', communityKey: 'diabetes' },
    { name: 'Panic Disorder', color: '#7c5cbf', communityKey: 'mental' },
  ];

  readonly defaultSymptoms = [
    'Fatigue',
    'Hair loss',
    'Weight gain',
    'Irregular periods',
    'Brain fog',
    'Anxiety',
    'Mood swings',
    'Acne',
    'Sleep issues',
  ];

  private readonly searchableTopics: SearchTopic[] = [
    { name: 'Blood Sugar', slug: 'blood-sugar', usageCount: 7, communityKey: 'diabetes' },
    { name: 'Insulin', slug: 'insulin', usageCount: 5, communityKey: 'diabetes' },
    { name: 'Metformin', slug: 'metformin', usageCount: 4, communityKey: 'diabetes' },
    { name: 'HbA1c', slug: 'hba1c', usageCount: 4, communityKey: 'diabetes' },
    { name: 'Hormones', slug: 'hormones', usageCount: 9, communityKey: 'pcos' },
    { name: 'Hair Loss', slug: 'hair-loss', usageCount: 8, communityKey: 'pcos' },
    { name: 'Periods', slug: 'periods', usageCount: 7, communityKey: 'pcos' },
    { name: 'Fertility', slug: 'fertility', usageCount: 6, communityKey: 'pcos' },
    { name: 'Acne', slug: 'acne', usageCount: 6, communityKey: 'skin' },
    { name: 'Acne Scars', slug: 'acne-scars', usageCount: 5, communityKey: 'skin' },
    { name: 'Skincare Routine', slug: 'skincare-routine', usageCount: 5, communityKey: 'skin' },
    { name: 'Sun Care', slug: 'sun-care', usageCount: 4, communityKey: 'skin' },
    { name: 'Dermatologist', slug: 'dermatologist', usageCount: 4, communityKey: 'skin' },
    { name: 'Anxiety', slug: 'anxiety', usageCount: 15, communityKey: 'mental' },
    { name: 'Depression', slug: 'depression', usageCount: 9, communityKey: 'mental' },
    { name: 'Panic Attacks', slug: 'panic-attacks', usageCount: 6, communityKey: 'mental' },
    { name: 'Sleep', slug: 'sleep', usageCount: 12, communityKey: 'mental' },
    { name: 'Medication', slug: 'medication', usageCount: 12, communityKey: 'thyroid' },
    { name: 'Testing', slug: 'testing', usageCount: 10, communityKey: 'thyroid' },
    { name: 'Fatigue', slug: 'fatigue', usageCount: 8, communityKey: 'thyroid' },
    { name: "Hashimoto's", slug: 'hashimotos', usageCount: 6, communityKey: 'thyroid' },
    { name: 'Brain Fog', slug: 'brain-fog', usageCount: 3, communityKey: 'thyroid' },
  ];

  readonly currentCommunity = computed(() => {
    const currentId = this.currentCommunityIdValue();
    const currentName = this.currentCommunityNameValue();
    const category = currentId ? this.findCategoryByIdentifier(currentId) : null;
    const sourceText = `${category?.slug ?? ''} ${category?.name ?? currentName ?? ''}`.trim();

    if (!sourceText) return null;

    return {
      id: category ? String(category.id) : currentId ?? '',
      name: category?.name ?? currentName ?? '',
      key: resolveCommunityColorKey(sourceText),
    };
  });

  readonly prioritizedConditions = computed(() => {
    const activeKey = this.currentCommunity()?.key;
    if (!activeKey || this.contextValue() !== 'community') return this.defaultConditions;

    const prioritized = this.defaultConditions.filter(condition => condition.communityKey === activeKey);
    const remainder = this.defaultConditions.filter(condition => condition.communityKey !== activeKey);
    return [...prioritized, ...remainder];
  });

  readonly communityTopics = computed(() => {
    const activeCommunity = this.currentCommunity();
    if (!activeCommunity || this.contextValue() !== 'community') return [] as SearchTagResult[];

    return this.searchableTopics
      .filter(topic => topic.communityKey === activeCommunity.key)
      .slice(0, 6)
      .map(topic => ({
        name: topic.name,
        highlightedName: this.highlightMatch(topic.name, ''),
        slug: topic.slug,
        usageCount: topic.usageCount,
        communityId: activeCommunity.id,
        communityName: activeCommunity.name,
        communityKey: activeCommunity.key,
      }));
  });

  readonly matchingCommunities = computed<SearchCommunityResult[]>(() => {
    const rawQuery = this.searchQueryValue().trim();
    const query = rawQuery.toLowerCase();
    if (!query) return [];

    return this.categoriesStore.allCategoryCards()
      .filter(category => {
        const searchable = [category.name, category.tagline, category.slug ?? ''].join(' ').toLowerCase();
        return searchable.includes(query);
      })
      .slice(0, 5)
      .map(category => ({
        id: category.id,
        name: category.name,
        highlightedName: this.highlightMatch(category.name, rawQuery),
        slug: category.slug,
        memberCount: category.members,
        postCount: category.today,
        communityKey: this.resolveCommunityKey(category),
      }));
  });

  readonly matchingTags = computed<SearchTagResult[]>(() => {
    const rawQuery = this.searchQueryValue().trim();
    const query = rawQuery.toLowerCase();
    if (!query) return [];

    return this.searchableTopics
      .filter(topic => topic.name.toLowerCase().includes(query))
      .map(topic => {
        const community = this.findCommunityByKey(topic.communityKey);
        if (!community) return null;

        return {
          name: topic.name,
          highlightedName: this.highlightMatch(topic.name, rawQuery),
          slug: topic.slug,
          usageCount: topic.usageCount,
          communityId: community.id,
          communityName: community.name,
          communityKey: topic.communityKey,
        };
      })
      .filter((topic): topic is SearchTagResult => Boolean(topic))
      .slice(0, 6);
  });

  readonly highlightedMatchingPosts = computed<SearchPostResult[]>(() => {
    const rawQuery = this.searchQueryValue().trim();
    return this.matchingPosts().map(post => ({
      ...post,
      highlightedTitle: this.highlightMatch(post.title, rawQuery),
    }));
  });

  readonly showNoResults = computed(() => {
    const query = this.searchQueryValue().trim();
    if (query.length < 2 || this.isSearching()) return false;

    return this.matchingCommunities().length === 0
      && this.matchingTags().length === 0
      && this.matchingPosts().length === 0;
  });

  ngOnInit(): void {
    this.categoriesStore.loadFromApi();
    this.recentSearches.set(this.searchService.getRecentSearches());

    this.searchSubject
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap(query => {
          const trimmed = query.trim();
          if (trimmed.length < 2) {
            this.matchingPosts.set([]);
            this.isSearching.set(false);
            return of<SearchPostResult[]>([]);
          }

          this.isSearching.set(true);
          return this.searchService.search(trimmed, undefined, 1, 5).pipe(
            map(response => this.extractSearchPosts(response)),
            catchError(() => of<SearchPostResult[]>([])),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(posts => {
        this.matchingPosts.set(posts);
        this.isSearching.set(false);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectCondition(condition: SearchCondition, event: MouseEvent): void {
    event.preventDefault();
    this.saveRecentSearch(condition.name);

    const community = this.findCommunityByKey(condition.communityKey);
    if (community) {
      this.emitSelection({
        kind: 'community',
        term: condition.name,
        route: ['/category', community.id],
      });
      return;
    }

    this.emitQuerySelection(condition.name);
  }

  selectSymptom(symptom: string, event: MouseEvent): void {
    event.preventDefault();
    this.emitQuerySelection(symptom);
  }

  selectRecentSearch(term: string, event: MouseEvent): void {
    event.preventDefault();
    this.emitQuerySelection(term);
  }

  clearRecentSearches(event: MouseEvent): void {
    event.preventDefault();
    this.searchService.clearRecentSearches();
    this.recentSearches.set([]);
  }

  navigateToCommunity(community: SearchCommunityResult, event: MouseEvent): void {
    event.preventDefault();
    this.saveRecentSearch(community.name);
    this.emitSelection({
      kind: 'community',
      term: community.name,
      route: ['/category', community.id],
    });
  }

  navigateToTag(tag: SearchTagResult, event: MouseEvent): void {
    event.preventDefault();
    this.saveRecentSearch(tag.name);
    this.emitSelection({
      kind: 'topic',
      term: tag.name,
      route: ['/category', tag.communityId],
      params: { tag: tag.slug },
    });
  }

  navigateToPost(post: SearchPostResult, event: MouseEvent): void {
    event.preventDefault();
    this.saveRecentSearch(post.title);
    this.emitSelection({
      kind: 'post',
      term: post.title,
      route: ['/post', post.id],
    });
  }

  highlightMatch(text: string, query: string): string {
    // Security: escape HTML entities in text to prevent XSS
    const escapeHtml = (str: string): string =>
      str.replace(/&/g, '&amp;')
         .replace(/</g, '&lt;')
         .replace(/>/g, '&gt;')
         .replace(/"/g, '&quot;')
         .replace(/'/g, '&#39;');

    const safeText = escapeHtml(text);
    if (!query) return safeText;

    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (!escaped) return safeText;

    const regex = new RegExp(`(${escapeHtml(escaped)})`, 'gi');
    return safeText.replace(regex, '<mark>$1</mark>');
  }

  private emitQuerySelection(term: string): void {
    this.saveRecentSearch(term);
    this.emitSelection({
      kind: 'query',
      term,
      route: ['/search'],
      params: { q: term },
    });
  }

  private emitSelection(selection: SearchSelection): void {
    this.suggestionSelected.emit(selection);
  }

  private saveRecentSearch(term: string): void {
    this.recentSearches.set(this.searchService.saveRecentSearch(term));
  }

  private extractSearchPosts(response: SearchResponse): SearchPostResult[] {
    const posts = (response?.posts ?? response?.data?.posts ?? []) as Array<Record<string, unknown>>;

    return posts
      .map(post => {
        const id = String(post['id'] ?? '');
        const title = String(post['title'] ?? post['subject'] ?? '').trim();
        if (!id || !title) return null;

        return {
          id,
          title,
          highlightedTitle: title,
          authorName: String(post['authorDisplayName'] ?? post['authorName'] ?? post['author'] ?? 'Member'),
          replyCount: Number(post['replyCount'] ?? post['commentCount'] ?? post['commentsCount'] ?? 0),
        };
      })
      .filter((post): post is SearchPostResult => Boolean(post));
  }

  private findCommunityByKey(key: CommunityColorKey): CategoryCardVm | undefined {
    return this.categoriesStore.allCategoryCards().find(category => this.resolveCommunityKey(category) === key);
  }

  private findCategoryByIdentifier(identifier: string): CategoryCardVm | null {
    const normalized = (identifier ?? '').trim().toLowerCase();
    if (!normalized) return null;

    return this.categoriesStore.allCategoryCards().find(category =>
      category.id.toLowerCase() === normalized || (category.slug ?? '').toLowerCase() === normalized
    ) ?? null;
  }

  private resolveCommunityKey(category: CategoryCardVm): CommunityColorKey {
    return resolveCommunityColorKey(`${category.slug ?? ''} ${category.name}`);
  }
}
